"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, SlidersHorizontal, ArrowRight, FlaskConical, Pill, ShieldCheck, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
const powderSpecs = [{
  parameter: "Flowability & Shear Testing",
  standard: "ASTM D6128 / USP <1174>",
  metric: "Shear Cell & Aerated Density",
  status: "Pending Technical Review"
}, {
  parameter: "Tapped & Bulk Density",
  standard: "USP <616> Method I & II",
  metric: "Hausner Ratio / Carr Index",
  status: "Pending Technical Review"
}, {
  parameter: "Angle of Repose",
  standard: "ISO 4324 / Ph. Eur. 2.9.36",
  metric: "Dynamic & Static Flow Angle",
  status: "Pending Technical Review"
}, {
  parameter: "Moisture / Loss on Drying",
  standard: "USP <731> / EP 2.2.32",
  metric: "Halogen / IR Thermogravimetric",
  status: "Pending Technical Review"
}, {
  parameter: "Data Integrity & Compliance",
  standard: "Data Integrity & Audit Trail Support",
  metric: "Audit Trail & Electronic Signatures",
  status: "Pending Technical Review"
}];
const tabletSpecs = [{
  parameter: "Tablet Hardness & Breaking Force",
  standard: "USP <1217> / Ph. Eur. 2.9.8",
  metric: "Constant Speed / Constant Force (N / kP)",
  status: "Pending Technical Review"
}, {
  parameter: "Friability & Abrasion",
  standard: "USP <1216> / Ph. Eur. 2.9.7",
  metric: "Dual Drum 25 RPM (Automated Discharge)",
  status: "Pending Technical Review"
}, {
  parameter: "Disintegration Testing",
  standard: "USP <701> / Ph. Eur. 2.9.1",
  metric: "Auto-Endpoint Detection Basket",
  status: "Pending Technical Review"
}, {
  parameter: "Dissolution Testing Profiles",
  standard: "USP <711> Apparatus 1 & 2",
  metric: "Online UV/HPLC Automated Sampling",
  status: "Pending Technical Review"
}, {
  parameter: "Dimensional Metrology",
  standard: "Digital Contact & Laser Micrometry",
  metric: "Thickness, Diameter, Length +/-0.01mm",
  status: "Pending Technical Review"
}];
export default function InstrumentsComparison() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="testinginstrumentscomparison" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs uppercase font-semibold tracking-wider">
              <SlidersHorizontal className="size-3.5 text-primary" />
              Instrumentation Matrix
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Physical Testing Instrumentation Comparison
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Side-by-side technical evaluation across powder characterization and solid dosage testing platforms. Technical specifications below are configured for qualification workflows across Ireland and the UK.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-muted/60 border border-border text-xs text-muted-foreground">
            <FileText className="size-3.5 text-primary" />
            <span>Note: Neutral baseline specifications currently marked for technical review and client protocol alignment.</span>
          </div>
        </div>

        {/* 2-Column Comparison Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Column 1: Powder Testing */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="w-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
              <div>
                <CardHeader className="border-b border-border bg-muted/20 pb-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <FlaskConical className="size-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-card-foreground font-default">
                          Powder Testing Instruments
                        </CardTitle>
                        <CardDescription className="text-xs sm:text-sm mt-1">
                          Raw material characterization, bulk properties & flow mechanics
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 shrink-0">
                      Solid State
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="pt-6 space-y-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground bg-muted/40 p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="size-4 text-primary" />
                      <span className="font-medium text-foreground">Pharmacopeial Harmonization:</span>
                    </div>
                    <span>USP, Ph. Eur., JP & ASTM compliant</span>
                  </div>

                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead className="font-semibold text-xs text-foreground">Testing Parameter</TableHead>
                          <TableHead className="font-semibold text-xs text-foreground">Regulatory Standard</TableHead>
                          <TableHead className="font-semibold text-xs text-foreground">Technical Specification</TableHead>
                          <TableHead className="text-right font-semibold text-xs text-foreground">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {powderSpecs.map((spec, i) => <TableRow key={spec.parameter} data-index={i} className="text-xs">
                            <TableCell className="font-medium text-foreground py-3">
                              {spec.parameter}
                            </TableCell>
                            <TableCell className="text-muted-foreground py-3">
                              {spec.standard}
                            </TableCell>
                            <TableCell className="text-foreground py-3">
                              {spec.metric}
                            </TableCell>
                            <TableCell className="text-right py-3">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded cursor-help">
                                      <HelpCircle className="size-3 text-primary" />
                                      Review
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Neutral placeholder spec marked for site-specific technical review.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="space-y-2 pt-2">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
                      Standard Qualification Deliverables:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Traceable calibration standards</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>IQ/OQ/PQ protocol execution</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Data integrity and audit trail support</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Preventative maintenance plan</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </div>

              <CardFooter className="border-t border-border bg-muted/10 pt-4 pb-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
                  <Link to="/PowderTestingPage" className="inline-flex items-center justify-center gap-1.5">
                    Explore Powder Systems
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild className="w-full sm:w-auto text-xs text-muted-foreground hover:text-foreground">
                  <Link to="/ContactPage">Request Qualification Protocol</Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Column 2: Tablet Testing */}
          <motion.div variants={itemVariants} className="flex">
            <Card className="w-full flex flex-col justify-between border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
              <div>
                <CardHeader className="border-b border-border bg-muted/20 pb-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                        <Pill className="size-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-card-foreground font-default">
                          Tablet Testing Instruments
                        </CardTitle>
                        <CardDescription className="text-xs sm:text-sm mt-1">
                          Finished oral dosage form analysis, physical strength & dissolution
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 shrink-0">
                      Finished Dose
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="pt-6 space-y-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground bg-muted/40 p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="size-4 text-primary" />
                      <span className="font-medium text-foreground">Pharmacopeial Harmonization:</span>
                    </div>
                    <span>USP, Ph. Eur., BP & IP standards</span>
                  </div>

                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead className="font-semibold text-xs text-foreground">Testing Parameter</TableHead>
                          <TableHead className="font-semibold text-xs text-foreground">Regulatory Standard</TableHead>
                          <TableHead className="font-semibold text-xs text-foreground">Technical Specification</TableHead>
                          <TableHead className="text-right font-semibold text-xs text-foreground">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tabletSpecs.map((spec, i) => <TableRow key={spec.parameter} data-index={i} className="text-xs">
                            <TableCell className="font-medium text-foreground py-3">
                              {spec.parameter}
                            </TableCell>
                            <TableCell className="text-muted-foreground py-3">
                              {spec.standard}
                            </TableCell>
                            <TableCell className="text-foreground py-3">
                              {spec.metric}
                            </TableCell>
                            <TableCell className="text-right py-3">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded cursor-help">
                                      <HelpCircle className="size-3 text-primary" />
                                      Review
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Neutral placeholder spec marked for site-specific technical review.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="space-y-2 pt-2">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
                      Standard Qualification Deliverables:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Mechanical calibration tools</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Temperature & speed verification</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>Software data integrity audit</span>
                      </li>
                      <li className="flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                        <span>On-site operator certification</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </div>

              <CardFooter className="border-t border-border bg-muted/10 pt-4 pb-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
                  <Link to="/TabletTestingPage" className="inline-flex items-center justify-center gap-1.5">
                    Explore Tablet Systems
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild className="w-full sm:w-auto text-xs text-muted-foreground hover:text-foreground">
                  <Link to="/ContactPage">Request Qualification Protocol</Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </motion.div>

        {/* Technical Assistance Callout Banner */}
        <div className="mt-12 p-6 rounded-xl bg-secondary border border-border text-secondary-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-base font-semibold font-default text-foreground">
              Require custom test parameters or method development?
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Antech Solutions provides regulatory application support, IQ/OQ qualification protocols, and vendor-certified service across Ireland and the UK.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button variant="default" asChild className="w-full sm:w-auto">
              <Link to="/ContactPage">Consult Technical Team</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}